import { Testimonial, TestimonialsConfig } from '../types';
import { db } from './firebase';
import { collection, doc, getDocs, setDoc, deleteDoc, getDoc, onSnapshot, query, orderBy, Unsubscribe } from 'firebase/firestore';
import { perfTracker } from './perfLogger';

const TESTIMONIALS_COLLECTION = 'testimonials';
const CONFIG_DOC_PATH = 'settings/testimonials_config';

export const DEFAULT_TESTIMONIALS_CONFIG: TestimonialsConfig = {
  autoSlideSpeedMs: 5000,
  trustpilotRating: 4.9,
  trustpilotReviewCount: 1280,
  trustpilotUrl: 'https://www.trustpilot.com/review/rheditography.net',
  trustpilotAutoSyncEnabled: false,
  trustpilotApiKey: '',
  trustpilotBusinessUnitId: '',
  syncFrequencyHours: 24,
  lastSyncedAt: new Date().toISOString(),
};

export const INITIAL_ENTERPRISE_TESTIMONIALS: Testimonial[] = [
  {
    id: 't-1',
    customerName: 'David Miller',
    customerPhoto: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=300&q=80',
    country: 'United States',
    countryCode: 'US',
    reviewText: "RH Editography provides the cleanest 3D PSD document templates I've ever used. Ultra-fast download speed and perfectly organized layered AI files saved our design agency dozens of hours.",
    starRating: 5,
    isFeatured: true,
    sortOrder: 1,
    isVisible: true,
    verifiedCustomer: true,
    createdAt: '2026-07-20T10:15:00Z',
    source: 'admin'
  },
  {
    id: 't-2',
    customerName: 'Sophia Martinez',
    customerPhoto: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?auto=format&fit=crop&w=300&q=80',
    country: 'United Kingdom',
    countryCode: 'GB',
    reviewText: "Top quality passport and ID vector templates. Instant delivery right after instant payment, and incredible customer support when I needed custom font installation guidance.",
    starRating: 5,
    isFeatured: true,
    sortOrder: 2,
    isVisible: true,
    verifiedCustomer: true,
    createdAt: '2026-07-18T14:30:00Z',
    source: 'admin'
  },
  {
    id: 't-3',
    customerName: 'Alexander Schmidt',
    customerPhoto: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=300&q=80',
    country: 'Germany',
    countryCode: 'DE',
    reviewText: "The driving license and utility bill PSD templates are mathematically precise and fully editable. Highly recommended for digital design professionals and agency creators!",
    starRating: 5,
    isFeatured: true,
    sortOrder: 3,
    isVisible: true,
    verifiedCustomer: true,
    createdAt: '2026-07-15T09:00:00Z',
    source: 'admin'
  },
  {
    id: 't-4',
    customerName: 'Jean-Luc Dubois',
    customerPhoto: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=300&q=80',
    country: 'France',
    countryCode: 'FR',
    reviewText: "Unbeatable precision and file quality! The automated verification system worked seamlessly, and I received my high-resolution vector download package within seconds.",
    starRating: 5,
    isFeatured: true,
    sortOrder: 4,
    isVisible: true,
    verifiedCustomer: true,
    createdAt: '2026-07-10T16:45:00Z',
    source: 'admin'
  },
  {
    id: 't-5',
    customerName: 'Carlos Silva',
    customerPhoto: 'https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?auto=format&fit=crop&w=300&q=80',
    country: 'Brazil',
    countryCode: 'BR',
    reviewText: "RH Editography is my go-to online marketplace for high resolution digital document designs. Excellent layered Photoshop files complete with free font links and guides.",
    starRating: 5,
    isFeatured: true,
    sortOrder: 5,
    isVisible: true,
    verifiedCustomer: true,
    createdAt: '2026-07-05T11:20:00Z',
    source: 'admin'
  },
  {
    id: 't-6',
    customerName: 'Kenji Takahashi',
    customerPhoto: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=300&q=80',
    country: 'Japan',
    countryCode: 'JP',
    reviewText: "Exceptionally high resolution graphics, lightning fast server responses, and flawless vector layout structure. Absolute 10/10 enterprise service!",
    starRating: 5,
    isFeatured: true,
    sortOrder: 6,
    isVisible: true,
    verifiedCustomer: true,
    createdAt: '2026-07-01T08:10:00Z',
    source: 'admin'
  }
];

export function getLocalCachedTestimonials(): Testimonial[] {
  try {
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem('rh_cached_testimonials');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    }
  } catch (e) {}
  return INITIAL_ENTERPRISE_TESTIMONIALS;
}

export function getLocalCachedTestimonialsConfig(): TestimonialsConfig {
  try {
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem('rh_cached_testimonials_config');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') {
          return { ...DEFAULT_TESTIMONIALS_CONFIG, ...parsed };
        }
      }
    }
  } catch (e) {}
  return DEFAULT_TESTIMONIALS_CONFIG;
}

export async function fetchTestimonialsFromFirestore(): Promise<Testimonial[]> {
  const startTime = performance.now();
  try {
    const colRef = collection(db, TESTIMONIALS_COLLECTION);
    const q = query(colRef, orderBy('sortOrder', 'asc'));
    const snapshot = await getDocs(q);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(TESTIMONIALS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'server', snapshot.size);

    if (!snapshot.empty) {
      const items: Testimonial[] = [];
      snapshot.forEach(docSnap => {
        items.push({ id: docSnap.id, ...docSnap.data() } as Testimonial);
      });
      if (typeof window !== 'undefined') {
        localStorage.setItem('rh_cached_testimonials', JSON.stringify(items));
      }
      return items;
    }
  } catch (err) {
    console.warn('Firestore fetch testimonials fallback:', err);
  }
  return getLocalCachedTestimonials();
}

export async function saveTestimonialToFirestore(testimonial: Testimonial): Promise<void> {
  try {
    const docRef = doc(db, TESTIMONIALS_COLLECTION, testimonial.id);
    await setDoc(docRef, testimonial, { merge: true });
  } catch (err) {
    console.warn('Firestore save testimonial fallback:', err);
  }
  try {
    if (typeof window !== 'undefined') {
      const current = getLocalCachedTestimonials();
      const existingIdx = current.findIndex(t => t.id === testimonial.id);
      if (existingIdx >= 0) {
        current[existingIdx] = testimonial;
      } else {
        current.push(testimonial);
      }
      current.sort((a, b) => a.sortOrder - b.sortOrder);
      localStorage.setItem('rh_cached_testimonials', JSON.stringify(current));
    }
  } catch (e) {}
}

export async function deleteTestimonialFromFirestore(id: string): Promise<void> {
  try {
    const docRef = doc(db, TESTIMONIALS_COLLECTION, id);
    await deleteDoc(docRef);
  } catch (err) {
    console.warn('Firestore delete testimonial fallback:', err);
  }
  try {
    if (typeof window !== 'undefined') {
      const current = getLocalCachedTestimonials().filter(t => t.id !== id);
      localStorage.setItem('rh_cached_testimonials', JSON.stringify(current));
    }
  } catch (e) {}
}

const testimonialSubscribers = new Set<(testimonials: Testimonial[]) => void>();
let activeTestimonialsUnsub: Unsubscribe | null = null;

export function subscribeToTestimonialsInFirestore(onUpdate: (testimonials: Testimonial[]) => void): () => void {
  onUpdate(getLocalCachedTestimonials());

  testimonialSubscribers.add(onUpdate);

  fetchTestimonialsFromFirestore().then((items) => {
    if (items && items.length > 0) {
      testimonialSubscribers.forEach((cb) => {
        try { cb(items); } catch (e) {}
      });
    }
  }).catch((err) => {
    console.warn('One-time testimonials fetch error:', err);
  });

  return () => {
    testimonialSubscribers.delete(onUpdate);
  };
}

export async function fetchTestimonialsConfigFromFirestore(): Promise<TestimonialsConfig> {
  const startTime = performance.now();
  try {
    const docRef = doc(db, CONFIG_DOC_PATH);
    const snap = await getDoc(docRef);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(CONFIG_DOC_PATH, duration, snap.metadata.fromCache ? 'cache' : 'server', 1);

    if (snap.exists()) {
      const data = snap.data() as TestimonialsConfig;
      const merged = { ...DEFAULT_TESTIMONIALS_CONFIG, ...data };
      if (typeof window !== 'undefined') {
        localStorage.setItem('rh_cached_testimonials_config', JSON.stringify(merged));
      }
      return merged;
    }
  } catch (err) {
    console.warn('Firestore config fetch fallback:', err);
  }
  return getLocalCachedTestimonialsConfig();
}

export async function saveTestimonialsConfigToFirestore(config: TestimonialsConfig): Promise<void> {
  try {
    const docRef = doc(db, CONFIG_DOC_PATH);
    await setDoc(docRef, config, { merge: true });
  } catch (err) {
    console.warn('Firestore config save fallback:', err);
  }
  try {
    if (typeof window !== 'undefined') {
      localStorage.setItem('rh_cached_testimonials_config', JSON.stringify(config));
    }
  } catch (e) {}
}

const configSubscribers = new Set<(config: TestimonialsConfig) => void>();
let activeConfigUnsub: Unsubscribe | null = null;

export function subscribeToTestimonialsConfigInFirestore(onUpdate: (config: TestimonialsConfig) => void): () => void {
  onUpdate(getLocalCachedTestimonialsConfig());

  configSubscribers.add(onUpdate);

  fetchTestimonialsConfigFromFirestore().then((cfg) => {
    if (cfg) {
      configSubscribers.forEach((cb) => {
        try { cb(cfg); } catch (e) {}
      });
    }
  }).catch((err) => {
    console.warn('One-time testimonials config fetch error:', err);
  });

  return () => {
    configSubscribers.delete(onUpdate);
  };
}
